// 3D 틸트 + 스포트라이트 카드
import { forwardRef, useRef } from 'react'
import type { HTMLAttributes, MouseEvent } from 'react'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tilt?: boolean
  spotlight?: boolean
  glass?: boolean
  as?: 'div' | 'article' | 'section'
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { tilt = true, spotlight = true, glass = false, as: As = 'div', className = '', children, onMouseMove, onMouseLeave, ...rest },
  ref,
) {
  const inner = useRef<HTMLDivElement | null>(null)
  const setRef = (el: HTMLDivElement | null) => {
    inner.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el
  }

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    onMouseMove?.(e)
    const el = inner.current
    if (!el || prefersReducedMotion()) return
    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const px = (x / rect.width) * 100
    const py = (y / rect.height) * 100
    el.style.setProperty('--spot-x', `${px}%`)
    el.style.setProperty('--spot-y', `${py}%`)
    if (tilt) {
      const rx = ((y - rect.height / 2) / rect.height) * -6
      const ry = ((x - rect.width / 2) / rect.width) * 6
      el.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`
    }
  }
  const handleLeave = (e: MouseEvent<HTMLDivElement>) => {
    onMouseLeave?.(e)
    const el = inner.current
    if (!el) return
    el.style.transform = ''
  }

  const classes = ['card']
  if (glass) classes.push('glass')
  if (spotlight) classes.push('spot')
  if (className) classes.push(className)

  return (
    <As
      ref={setRef as any}
      className={classes.join(' ')}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      {...rest}
    >
      {children}
    </As>
  )
})
